/**
 * Serial-number detection + canvas blurring.
 *
 * Lazy-loads tesseract.js so it doesn't inflate the initial bundle.
 * Tesseract runs in a Web Worker; this file stays in the main thread.
 */

export interface DetectedRegion {
  id: string;
  text: string;
  /** Normalised to 0-1 relative to natural image dimensions */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Whether the user wants this region blurred */
  blur: boolean;
}

/** Patterns that look like firearm serial numbers */
const SERIAL_PATTERNS = [
  /^[A-Z]{1,3}[0-9]{5,12}$/, // e.g. AB1234567
  /^[0-9]{5,12}[A-Z]{1,3}$/, // e.g. 1234567AB
  /^[A-Z0-9]{6,16}$/, // generic alphanumeric run
  /^[A-Z]{2,4}-?[0-9]{4,10}$/, // e.g. USMC-123456
  /^[0-9]{2}[A-Z]{1,2}[0-9]{4,8}$/ // e.g. 12AB34567
]

const COMMON_WORDS = new Set([
  'MADE', 'USA', 'INC', 'LLC', 'MODEL', 'CAL', 'MM', 'ACP',
  'NATO', 'CORP', 'MFG', 'GUN', 'ARMS', 'FIRE', 'SAFE'
])

function looksLikeSerial (text: string): boolean {
  const t = text.trim().toUpperCase().replace(/\s+/g, '')
  if (t.length < 5 || t.length > 20) return false
  if (COMMON_WORDS.has(t)) return false
  return SERIAL_PATTERNS.some((re) => re.test(t))
}

export type OcrStatus = 'idle' | 'loading' | 'recognising' | 'done' | 'error';

export async function detectSerialRegions (
  file: File,
  onStatus?: (s: OcrStatus) => void
): Promise<DetectedRegion[]> {
  onStatus?.('loading')

  // Dynamic import – tesseract.js (~4 MB) only loads when the user requests it.
  const { createWorker } = await import('tesseract.js')
  const worker = await createWorker('eng', 1, {
    // Use OEM_LSTM_ONLY for speed
  })

  onStatus?.('recognising')

  const url = URL.createObjectURL(file)
  const { data } = await worker.recognize(url, {}, { blocks: true })
  URL.revokeObjectURL(url)
  await worker.terminate()

  onStatus?.('done')

  // Build a temporary image to get natural dimensions for normalising coords
  const img = await loadImage(file)
  const natW = img.naturalWidth
  const natH = img.naturalHeight

  const regions: DetectedRegion[] = []

  // `data.blocks` → paragraphs → lines → words
  for (const block of data.blocks ?? []) {
    for (const para of block.paragraphs ?? []) {
      for (const line of para.lines ?? []) {
        for (const word of line.words ?? []) {
          if (!word.text || !looksLikeSerial(word.text)) continue
          const bb = word.bbox
          regions.push({
            id: crypto.randomUUID(),
            text: word.text,
            x: bb.x0 / natW,
            y: bb.y0 / natH,
            w: (bb.x1 - bb.x0) / natW,
            h: (bb.y1 - bb.y0) / natH,
            blur: true
          })
        }
      }
    }
  }

  return regions
}

function loadImage (file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = reject
    img.src = url
  })
}

/**
 * Renders the image onto a canvas, applies a pixelate-blur over each
 * region where `blur === true`, and returns a new File.
 */
export async function applyBlurs (
  file: File,
  regions: DetectedRegion[]
): Promise<File> {
  const toBlur = regions.filter((r) => r.blur)
  if (toBlur.length === 0) return file

  const img = await loadImage(file)
  const { naturalWidth: W, naturalHeight: H } = img

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, W, H)

  for (const region of toBlur) {
    const rx = Math.floor(region.x * W)
    const ry = Math.floor(region.y * H)
    const rw = Math.ceil(region.w * W)
    const rh = Math.ceil(region.h * H)

    // Expand bounding box slightly so the blur covers engraving depth halos
    const pad = Math.max(4, Math.round(rh * 0.25))
    const bx = Math.max(0, rx - pad)
    const by = Math.max(0, ry - pad)
    const bw = Math.min(W - bx, rw + pad * 2)
    const bh = Math.min(H - by, rh + pad * 2)

    pixelateRegion(ctx, bx, by, bw, bh)
  }

  return canvasToFile(canvas, file.name, file.type)
}

/** Heavy pixelation that looks intentional rather than accidental */
function pixelateRegion (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  blockSize = 14
) {
  const imageData = ctx.getImageData(x, y, w, h)
  const { data, width, height } = imageData

  for (let py = 0; py < height; py += blockSize) {
    for (let px = 0; px < width; px += blockSize) {
      // Sample the colour of the top-left pixel in this block
      const i = (py * width + px) * 4
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]

      // Fill the whole block with that colour
      for (let dy = 0; dy < blockSize && py + dy < height; dy++) {
        for (let dx = 0; dx < blockSize && px + dx < width; dx++) {
          const j = ((py + dy) * width + (px + dx)) * 4
          data[j] = r
          data[j + 1] = g
          data[j + 2] = b
        }
      }
    }
  }

  ctx.putImageData(imageData, x, y)
}

function canvasToFile (canvas: HTMLCanvasElement, name: string, originalType: string): Promise<File> {
  const lower = (originalType || '').toLowerCase()
  const mime =
    lower === 'image/png' ? 'image/png' : lower === 'image/webp' ? 'image/webp' : 'image/jpeg'
  const base = name.replace(/\.[^/.]+$/, '') || 'photo'
  const outName =
    mime === 'image/jpeg' ? `${base}.jpg` : mime === 'image/png' ? `${base}.png` : `${base}.webp`

  return new Promise((resolve, reject) => {
    const done = (blob: Blob | null) => {
      if (!blob) {
        reject(new Error('Canvas toBlob failed — try “Upload without blurring” or a JPEG/PNG photo.'))
        return
      }
      resolve(new File([blob], outName, { type: mime }))
    }
    if (mime === 'image/jpeg') {
      canvas.toBlob(done, mime, 0.92)
    } else {
      canvas.toBlob(done, mime)
    }
  })
}
